/**
 * Org cross-table management API client.
 *
 * Mirrors the Plan-01 backend endpoints exactly (22-01-PLAN.md wire shapes).
 * All hooks follow the api/extractions.ts react-query idiom.
 *
 * Endpoints (all org-scoped via JWT bearer — no org id in client code):
 *   GET  /api/org/cross-tables               → OrgCrossTableSummary[]
 *   GET  /api/org/cross-tables/{xtable}       → OrgCrossTableValuesPage
 *   DELETE /api/org/cross-tables/{xtable}     → 204
 *   GET  /api/org/cross-tables/readiness      → CrossTableReadiness
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";

// ──────────────────────── Wire types ──────────────────────────────────────────

/** One cross-table summary row from GET /api/org/cross-tables */
export interface OrgCrossTableSummary {
  name: string;
  row_count: number;
  source_file: string;
  updated_at: string;
}

/** One value row from GET /api/org/cross-tables/{xtable} */
export interface OrgCrossTableValueRow {
  elmnt_value: string;
  mapped_value: string;
  mapped_value2: string | null;
}

/** Paged values response from GET /api/org/cross-tables/{xtable} */
export interface OrgCrossTableValuesPage {
  xtable: string;
  total: number;
  items: OrgCrossTableValueRow[];
}

/** Readiness check from GET /api/org/cross-tables/readiness?template=<class_or_id> */
export interface CrossTableReadiness {
  required: string[];
  loaded: string[];
  missing: string[];
  fallback: boolean;
}

// ──────────────────────── Hooks ───────────────────────────────────────────────

const PAGE_SIZE = 50;

/** List all cross-tables for the org (name, row_count, source_file, updated_at). */
export function useOrgCrossTables() {
  return useQuery({
    queryKey: ["org-cross-tables"],
    queryFn: async (): Promise<OrgCrossTableSummary[]> => {
      const { data } = await api.get<OrgCrossTableSummary[]>("/org/cross-tables");
      return data;
    },
  });
}

/** Paged values for a single cross-table. enabled only when xtable is non-empty. */
export function useOrgCrossTableValues(
  xtable: string,
  { offset = 0, limit = PAGE_SIZE }: { offset?: number; limit?: number } = {},
) {
  return useQuery({
    queryKey: ["org-cross-tables", xtable, offset],
    queryFn: async (): Promise<OrgCrossTableValuesPage> => {
      const encoded = encodeURIComponent(xtable);
      const { data } = await api.get<OrgCrossTableValuesPage>(
        `/org/cross-tables/${encoded}?limit=${limit}&offset=${offset}`,
      );
      return data;
    },
    enabled: !!xtable,
  });
}

/** Delete a cross-table (admin only on the server). Invalidates the list on success. */
export function useDeleteOrgCrossTable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (xtable: string): Promise<void> => {
      const encoded = encodeURIComponent(xtable);
      await api.delete(`/org/cross-tables/${encoded}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org-cross-tables"] });
    },
  });
}

/**
 * Cross-table readiness for a given format template, optionally book-aware for a specific config.
 * Returns the required/loaded/missing sets and the fallback flag. `enabled` only when template is
 * non-empty. When `configId` is supplied the server computes "loaded" from that config's attached
 * code book + per-config overrides (Phase 28 SC3) instead of the bare org store.
 */
export function useCrossTableReadiness(template: string | undefined, configId?: string) {
  return useQuery({
    queryKey: ["org-cross-tables", "readiness", template, configId ?? null],
    queryFn: async (): Promise<CrossTableReadiness> => {
      const params = new URLSearchParams({ template: template! });
      if (configId) params.set("config_id", configId);
      const { data } = await api.get<CrossTableReadiness>(`/org/cross-tables/readiness?${params}`);
      return data;
    },
    enabled: !!template,
  });
}
